/**
 * Zustand store for the Infrastructure flow in step 3 of the New Environment wizard.
 *
 * Rules enforced here, so every component gets them for free:
 *  - Changing a location parent clears everything beneath it: Environment -> Region -> Resource group -> what depends on
 *    them (VNets and subnets, the existing servers listed and chosen, the SQL storage recommendation, add-on images).
 *  - The RDS server and the DB server are each Existing or Dedicated, independently. Changing one server's choice clears only that
 *    server's values. Process Pro Global's DB server is always Dedicated (`effectiveServerMode`).
 *  - A Dedicated RDS server of a product that uses the pool tiers is also asked how many RDS VMs it is (1 to 8, `rdsVmCount`). That number is
 *    the only one the free IPs, the quota check, the names and the request use; it goes back to its default with the server's other values.
 *  - A Dedicated RDS server and a Dedicated SQL server VM are each asked "Do you need a public IP for this server?" (default No). The answer is
 *    part of the server's own values, so it goes back to No with them whenever the server's choice (or the level) changes. An Existing server and
 *    an Azure SQL server have none. The public IPs wanted in all are `publicIpCount`: one per RDS VM of an RDS Yes, plus one for a DB Yes.
 *  - Changing the product resets all downstream selections and leaves a notice for the user.
 *  - Choosing "No" for an add-on clears whatever was entered for it. Answering No to the SQL instance question clears the name.
 *  - The license fields follow the environment's type (data/licenses.ts): a type change clears them, the same type keeps them.
 *  - Changing the product LINE (for example Traverse 11.0 Standard to 11.2 Global) clears only what depends on it: the SQL
 *    instance answer, the add-ons and the link choice. A change of family clears everything, as above.
 *
 * Nothing here is persisted in the browser (no storage middleware), so the license keys and passwords live in memory only.
 */
import { create } from 'zustand'
import { environmentTypeOf } from '@/data/environmentTypes'
import type { LinkVersion } from '@/data/linkVersions'
import { RDS_VM_COUNT_DEFAULT, RDS_VM_COUNT_MIN, clampRdsVmCount, type VmKind } from '@/data/sizeCatalog'
import { ALL_COMPONENT_KEYS, type TraverseComponentKey } from '@/data/traverseComponents'
import { hasProductLine, isProcessPro } from '@/data/productLines'
import type {
  DataDisk,
  ExistingServerSummary,
  ExistingSqlServer,
  IisServer,
  ImageChoice,
  InfraEnvironment,
  InfraLevel,
  InfraProduct,
  InfraRegion,
  InfraResourceGroup,
  InfraSubStep,
  NetworkChoice,
  PaasValues,
  VmSizeChoiceEntry,
  VmSizeChoices,
  ServerMode,
  SqlAuthType,
  SqlStorageConfig,
} from '@/types/infrastructure'
import { paasDefaults } from '@/utils/infraPaasFields'
import type { ServerSequence } from '@/utils/naming'
import { usesPoolTiers } from '@/utils/sizing'

export const INFRA_SUB_STEPS: InfraSubStep[] = ['location', 'level', 'servers', 'addons']

/** Customer Portal or a Traverse component (WMS, Trav API Portal, Web API Portal, Admin Portal): whether the customer uses it, and
 * the existing IIS server it runs on when it does. */
export interface IisAddOnData {
  enabled: boolean
  server: IisServer | null
}

export type IisAddOnKey = 'customerPortal' | TraverseComponentKey

export interface RdsServerData {
  /** null until chosen. Process Pro Global's DB server is always dedicated: see `effectiveServerMode`. */
  mode: ServerMode | null
  /** Existing: the server chosen from the resource group. */
  existing: ExistingServerSummary | null
  /** Dedicated: where it goes, and its data disks. Its name and operating system are derived. */
  network: NetworkChoice
  disks: DataDisk[]
  /**
   * Dedicated: how many RDS VMs are created together, RDS_VM_COUNT_MIN to RDS_VM_COUNT_MAX (data/sizeCatalog.ts). Only the products that use the
   * pool tiers are asked (`asksRdsVmCount`); for every other server it stays at the default and is not sent. Read it through `rdsVmCount`.
   */
  count: number
  /**
   * Dedicated: "Do you need a public IP for this server?" One answer for every RDS VM created together (one public IP each). Default No. Read it
   * through `rdsPublicIp`, which is false for any server that is not asked.
   */
  publicIp: boolean
}

export interface DedicatedSqlVmData {
  /** The latest SQL image, as fetched. Read-only. */
  image: ImageChoice | null
  network: NetworkChoice
  /** "Do you need a public IP for this server?" Default No. Read it through `dbPublicIp`. */
  publicIp: boolean
  /** Data, log and tempdb storage. Null until the recommendation has loaded, and again when the region changes. */
  storage: SqlStorageConfig | null
  authType: SqlAuthType
  adminUser: string
  adminPassword: string
  collation: string
}

export interface DedicatedAzureSqlData {
  network: NetworkChoice
  paas: PaasValues
}

export interface DbServerData {
  mode: ServerMode | null
  /** Existing: the server chosen. On the AppCentral level its instances are not fetched, so `instances` is empty. */
  existing: ExistingSqlServer | null
  /** Dedicated on the Domain level: a SQL server VM. */
  sqlVm: DedicatedSqlVmData
  /** Dedicated on the AppCentral level: a new Azure SQL server (PaaS). */
  azureSql: DedicatedAzureSqlData
}

/** "Do you need an ODBC connection / new SQL instance?" null until answered. Yes asks for the instance name. */
export interface SqlInstanceAnswer {
  required: boolean | null
  instanceName: string
}

/** Every license field; only the ones of the environment's type are shown, checked and sent (data/licenses.ts). */
export interface LicenseData {
  siteId: string
  licenseKey: string
  productionKey: string
  sandboxKey: string
}

export interface AddOnData {
  enabled: boolean
  goldenImage: ImageChoice | null
  network: NetworkChoice
}

export type AddOnKey = 'advancedAnalytics' | 'bartender'

/** Everything the flow has collected. Pure data: the validators and the payload builder take exactly this. */
export interface InfraWizardData {
  product: InfraProduct | null
  subStep: InfraSubStep
  environment: InfraEnvironment | null
  region: InfraRegion | null
  resourceGroup: InfraResourceGroup | null
  /** From the Environment section's Customer Code input (the key keeps its old name). */
  customerNumber: number | null
  /**
   * The next number of each server name stem the flow uses, as `getNextServerSequence` answered it, keyed by `sequenceKey`. The
   * validators and the payload read the generated names from here.
   */
  serverSequences: Record<string, ServerSequence>
  level: InfraLevel | null
  /** The RDS servers of the chosen resource group and region, as fetched. null until fetched. */
  existingRdsServers: ExistingServerSummary[] | null
  /** The DB servers of the chosen resource group and region, as fetched (with their instances, except on the AppCentral level). */
  existingDbServers: ExistingSqlServer[] | null
  rdsServer: RdsServerData
  dbServer: DbServerData
  sqlInstance: SqlInstanceAnswer
  /** Only used when the product has no naming rule and the SQL server name has to be typed. */
  manualSqlServerName: string
  license: LicenseData
  advancedAnalytics: AddOnData
  bartender: AddOnData
  /** The IIS servers that exist in the chosen environment and region, as fetched. null until fetched. */
  existingIisServers: IisServer[] | null
  customerPortal: IisAddOnData
  wms: IisAddOnData
  travApiPortal: IisAddOnData
  webApiPortal: IisAddOnData
  adminPortal: IisAddOnData
  /** The old or the new link. One answer for the whole deployment; null until answered. */
  linkVersion: LinkVersion | null
  /** What the user chose where a VM size cannot be built in the region and the nearest series of the same size was offered (VmSizeBanner). */
  vmSizeChoices: VmSizeChoices
  /** A message for the user, for example after the product changed and the selections were cleared. */
  notice: string | null
}

export const noNetwork = (): NetworkChoice => ({ vnet: null, subnet: null })

let diskSequence = 0

/** A fresh data disk. Drive letters start at the given letter. */
export function newDisk(partial: Partial<DataDisk> = {}): DataDisk {
  diskSequence += 1
  return { key: `disk-${diskSequence}`, lun: 0, sizeGb: 128, sku: 'Premium_LRS', caching: 'ReadOnly', driveLetter: 'E', ...partial }
}

export const emptyRdsServer = (mode: ServerMode | null = null): RdsServerData => ({
  mode,
  existing: null,
  network: noNetwork(),
  disks: [newDisk({ lun: 0, driveLetter: 'E' })],
  count: RDS_VM_COUNT_DEFAULT,
  publicIp: false,
})

export const emptyDbServer = (mode: ServerMode | null = null): DbServerData => ({
  mode,
  existing: null,
  sqlVm: { image: null, network: noNetwork(), publicIp: false, storage: null, authType: 'windows', adminUser: '', adminPassword: '', collation: 'SQL_Latin1_General_CP1_CI_AS' },
  azureSql: { network: noNetwork(), paas: paasDefaults() },
})

export const unansweredSqlInstance = (): SqlInstanceAnswer => ({ required: null, instanceName: '' })
export const emptyLicense = (): LicenseData => ({ siteId: '', licenseKey: '', productionKey: '', sandboxKey: '' })
const emptyAddOn = (): AddOnData => ({ enabled: false, goldenImage: null, network: noNetwork() })
const emptyIis = (): IisAddOnData => ({ enabled: false, server: null })

export function emptyData(): InfraWizardData {
  return {
    product: null,
    subStep: 'location',
    environment: null,
    region: null,
    resourceGroup: null,
    customerNumber: null,
    serverSequences: {},
    level: null,
    existingRdsServers: null,
    existingDbServers: null,
    rdsServer: emptyRdsServer(),
    dbServer: emptyDbServer(),
    sqlInstance: unansweredSqlInstance(),
    manualSqlServerName: '',
    license: emptyLicense(),
    advancedAnalytics: emptyAddOn(),
    bartender: emptyAddOn(),
    existingIisServers: null,
    customerPortal: emptyIis(),
    wms: emptyIis(),
    travApiPortal: emptyIis(),
    webApiPortal: emptyIis(),
    adminPortal: emptyIis(),
    linkVersion: null,
    vmSizeChoices: {},
    notice: null,
  }
}

/** The two servers a deployment asks about. */
export type ServerRole = 'rds' | 'db'

/**
 * Process Pro Global's DB server is always dedicated: the Existing option is not offered for it. Its RDS server is Existing or Dedicated,
 * as Traverse's is. Mirrors the backend's DEDICATED_ONLY_SERVERS.
 */
export const offersExistingServer = (product: InfraProduct | null, server: ServerRole): boolean => !(isProcessPro(product) && server === 'db')

/** The choice that applies to a server: always Dedicated for Process Pro Global's DB server, otherwise what was chosen (null until then). */
export function effectiveServerMode(product: InfraProduct | null, chosen: ServerMode | null, server: ServerRole): ServerMode | null {
  return offersExistingServer(product, server) ? chosen : 'dedicated'
}

/**
 * True when the RDS server is asked how many RDS VMs it is: a Dedicated one of a product whose RDS servers are built from the pool tiers (the
 * Traverse lines and Process Pro Global; `usesPoolTiers`), in every environment. Every other RDS server is one server, and sends no count.
 */
export const asksRdsVmCount = (data: Pick<InfraWizardData, 'product' | 'rdsServer'>): boolean =>
  usesPoolTiers(data.product) && effectiveServerMode(data.product, data.rdsServer.mode, 'rds') === 'dedicated'

/**
 * How many RDS VMs are built: the count that was chosen where it is asked (`asksRdsVmCount`), otherwise one. It is the one number that the
 * subnet's free IPs, the VM quota check, the names reserved and the request are all based on.
 */
export const rdsVmCount = (data: Pick<InfraWizardData, 'product' | 'rdsServer'>): number => (asksRdsVmCount(data) ? data.rdsServer.count : RDS_VM_COUNT_MIN)

/**
 * True when the RDS server is asked about a public IP: a Dedicated one of a product the Traverse pipeline builds (the Traverse lines and
 * Process Pro Global; `usesPoolTiers`), the only pipeline that reads the answer. Another product (Ross) is built by its own pipeline, which
 * ignores it, so it is not asked. An Existing server is not changed by this flow.
 */
export const asksRdsPublicIp = (data: Pick<InfraWizardData, 'product' | 'rdsServer'>): boolean =>
  usesPoolTiers(data.product) && effectiveServerMode(data.product, data.rdsServer.mode, 'rds') === 'dedicated'

/**
 * True when the DB server is asked about a public IP: a Dedicated SQL server VM (Domain level). Never an Existing server, and never the
 * AppCentral level's Azure SQL (PaaS) server, which is not a VM and cannot have one.
 */
export const asksDbPublicIp = (data: Pick<InfraWizardData, 'product' | 'level' | 'dbServer'>): boolean =>
  usesPoolTiers(data.product) && data.level === 'domain' && effectiveServerMode(data.product, data.dbServer.mode, 'db') === 'dedicated'

/** The RDS answer: Yes only for a server that is asked, so a stale answer of a server that was switched away never counts. */
export const rdsPublicIp = (data: Pick<InfraWizardData, 'product' | 'rdsServer'>): boolean => asksRdsPublicIp(data) && data.rdsServer.publicIp

/** The DB answer: Yes only for a Dedicated SQL server VM. */
export const dbPublicIp = (data: Pick<InfraWizardData, 'product' | 'level' | 'dbServer'>): boolean => asksDbPublicIp(data) && data.dbServer.sqlVm.publicIp

/**
 * How many public IPs the servers need in all: one for each RDS VM when the RDS answer is Yes (the answer covers every RDS VM created
 * together), plus one when the DB answer is Yes. 0 to 9. It is the one number the quota check asks about.
 */
export const publicIpCount = (data: Pick<InfraWizardData, 'product' | 'level' | 'rdsServer' | 'dbServer'>): number =>
  (rdsPublicIp(data) ? rdsVmCount(data) : 0) + (dbPublicIp(data) ? 1 : 0)

/** True for a product that has a product line: the three lines that are asked about IIS, the link and the license. */
export const asksLineQuestions = (product: InfraProduct | null): boolean => hasProductLine(product)

// ─── Clearing what depends on a location ─────────────────────────────────────

/** The existing servers listed and chosen, and an instance name that was checked against a chosen server. */
function clearExistingServers(data: InfraWizardData): Partial<InfraWizardData> {
  return {
    existingRdsServers: null,
    existingDbServers: null,
    rdsServer: { ...data.rdsServer, existing: null },
    dbServer: { ...data.dbServer, existing: null },
    sqlInstance: { ...data.sqlInstance, instanceName: '' },
  }
}

/** Networks and existing servers depend on the resource group. */
function clearResourceGroupDependent(data: InfraWizardData): Partial<InfraWizardData> {
  const existing = clearExistingServers(data)
  const rdsServer = existing.rdsServer as RdsServerData
  const dbServer = existing.dbServer as DbServerData
  return {
    ...existing,
    rdsServer: { ...rdsServer, network: noNetwork() },
    dbServer: { ...dbServer, sqlVm: { ...dbServer.sqlVm, network: noNetwork() }, azureSql: { ...dbServer.azureSql, network: noNetwork() } },
    advancedAnalytics: { ...data.advancedAnalytics, network: noNetwork() },
    bartender: { ...data.bartender, network: noNetwork() },
  }
}

/** The IIS servers belong to an environment and a region, so choosing another throws away the servers picked and listed. */
function clearIisServers(data: InfraWizardData): Partial<InfraWizardData> {
  const iis = Object.fromEntries((['customerPortal', ...ALL_COMPONENT_KEYS] as IisAddOnKey[]).map((key) => [key, { ...data[key], server: null }]))
  return { existingIisServers: null, ...(iis as Pick<InfraWizardData, IisAddOnKey>) }
}

/** Add-on images and the SQL storage recommendation are per region; so is everything beneath the region. */
function clearRegionDependent(data: InfraWizardData): Partial<InfraWizardData> {
  const rest = clearResourceGroupDependent(data)
  const dbServer = rest.dbServer as DbServerData
  return {
    ...rest,
    dbServer: { ...dbServer, sqlVm: { ...dbServer.sqlVm, storage: null } },
    advancedAnalytics: { ...(rest.advancedAnalytics as AddOnData), goldenImage: null },
    bartender: { ...(rest.bartender as AddOnData), goldenImage: null },
    ...clearIisServers(data),
  }
}

// ─── Changing the product line ───────────────────────────────────────────────

/** True when anything that depends on the product line has been answered or chosen. */
function hasLineDependentAnswers(data: InfraWizardData): boolean {
  return (
    data.sqlInstance.required !== null ||
    data.sqlInstance.instanceName !== '' ||
    data.advancedAnalytics.enabled ||
    data.bartender.enabled ||
    data.customerPortal.enabled ||
    ALL_COMPONENT_KEYS.some((key) => data[key].enabled) ||
    data.linkVersion !== null
  )
}

/** Everything that depends on the product line, back to unanswered. */
function clearLineDependent(): Partial<InfraWizardData> {
  return {
    sqlInstance: unansweredSqlInstance(),
    advancedAnalytics: emptyAddOn(),
    bartender: emptyAddOn(),
    customerPortal: emptyIis(),
    ...(Object.fromEntries(ALL_COMPONENT_KEYS.map((key) => [key, emptyIis()])) as Pick<InfraWizardData, TraverseComponentKey>),
    linkVersion: null,
  }
}

// ─── The store ───────────────────────────────────────────────────────────────

export interface InfraWizardActions {
  setProduct: (product: InfraProduct | null) => void
  setSubStep: (step: InfraSubStep) => void
  setCustomerNumber: (value: number | null) => void
  setServerSequence: (key: string, sequence: ServerSequence) => void
  dismissNotice: () => void
  setNotice: (notice: string) => void

  setEnvironment: (environment: InfraEnvironment | null) => void
  setRegion: (region: InfraRegion | null) => void
  setResourceGroup: (group: InfraResourceGroup | null) => void

  setLevel: (level: InfraLevel) => void
  setManualSqlServerName: (name: string) => void

  setExistingRdsServers: (servers: ExistingServerSummary[] | null) => void
  setExistingDbServers: (servers: ExistingSqlServer[] | null) => void
  /** Existing or Dedicated for the RDS server. A change clears that server's values, and only those. */
  setRdsMode: (mode: ServerMode) => void
  selectExistingRds: (server: ExistingServerSummary | null) => void
  patchRdsServer: (patch: Partial<Pick<RdsServerData, 'network' | 'disks'>>) => void
  /** How many RDS VMs the Dedicated RDS server is: a whole number from RDS_VM_COUNT_MIN to RDS_VM_COUNT_MAX, so anything else is moved into it. */
  setRdsVmCount: (count: number) => void
  /** "Do you need a public IP for this server?" for the Dedicated RDS server (all its RDS VMs). */
  setRdsPublicIp: (publicIp: boolean) => void
  /** Existing or Dedicated for the DB server. A change clears that server's values (and an instance name checked against it). */
  setDbMode: (mode: ServerMode) => void
  selectExistingDb: (server: ExistingSqlServer | null) => void
  patchDedicatedSqlVm: (patch: Partial<DedicatedSqlVmData>) => void
  patchDedicatedAzureSql: (patch: Partial<DedicatedAzureSqlData>) => void
  /** "Do you need a public IP for this server?" for the Dedicated SQL server VM. */
  setDbPublicIp: (publicIp: boolean) => void
  /** The way out of a public IP quota problem: every server answered Yes goes back to No, so nothing asks for one. */
  continueWithoutPublicIp: () => void

  setSqlInstanceRequired: (required: boolean) => void
  setSqlInstanceName: (name: string) => void
  patchLicense: (patch: Partial<LicenseData>) => void

  setAddOnEnabled: (key: AddOnKey, enabled: boolean) => void
  patchAddOn: (key: AddOnKey, patch: Partial<Omit<AddOnData, 'enabled'>>) => void
  setExistingIisServers: (servers: IisServer[] | null) => void
  setIisEnabled: (key: IisAddOnKey, enabled: boolean) => void
  selectIisServer: (key: IisAddOnKey, server: IisServer | null) => void
  setLinkVersion: (version: LinkVersion) => void
  chooseVmSize: (kind: VmKind, entry: VmSizeChoiceEntry) => void

  reset: () => void
}

export const useInfrastructureWizard = create<InfraWizardData & InfraWizardActions>((set, get) => ({
  ...emptyData(),

  setProduct: (product) => {
    const current = get().product
    if (product && current && product.slug === current.slug) {
      if (product.line !== current.line) {
        // Another line of the same family: only what depends on the line is cleared, and the user is told.
        const data = get()
        const cleared = hasLineDependentAnswers(data)
        // Process Pro Global's DB server is always dedicated, so the server choices made for another line are not carried over to it.
        const servers = isProcessPro(product) || isProcessPro(current) ? { rdsServer: emptyRdsServer(), dbServer: emptyDbServer() } : {}
        set({
          product,
          ...(cleared ? clearLineDependent() : {}),
          ...servers,
          // Someone on Add-ons is taken back to Servers when the line took an answer from it, so the question to answer is in view.
          ...(cleared && data.subStep === 'addons' && data.sqlInstance.required !== null ? { subStep: 'servers' as const } : {}),
          notice: cleared
            ? `The product line changed to ${product.name}, so your answers about the SQL instance, the add-ons and the link were cleared. Please answer them again.`
            : data.notice,
        })
        return
      }
      if (product.name !== current.name) set({ product })
      return
    }
    if (!product && !current) return
    set({
      ...emptyData(),
      product,
      notice: current
        ? `The product changed to ${product?.name ?? 'none'}, so your earlier infrastructure choices were cleared. Please choose them again.`
        : null,
    })
  },

  setSubStep: (subStep) => set({ subStep }),
  setCustomerNumber: (customerNumber) => set({ customerNumber }),
  setServerSequence: (key, sequence) => {
    const current = get().serverSequences[key]
    if (current && current.next === sequence.next && current.highestExisting === sequence.highestExisting) return
    set((s) => ({ serverSequences: { ...s.serverSequences, [key]: sequence } }))
  },
  dismissNotice: () => set({ notice: null }),
  setNotice: (notice) => set({ notice }),

  setEnvironment: (environment) => {
    const data = get()
    if (environment?.id === data.environment?.id) return
    // The license fields follow the environment's type: another type asks for other fields, so what was typed goes.
    const sameType = environmentTypeOf(environment) === environmentTypeOf(data.environment)
    set({
      environment,
      region: null,
      resourceGroup: null,
      serverSequences: {},
      ...clearRegionDependent(data),
      ...(sameType ? {} : { license: emptyLicense() }),
    })
  },

  setRegion: (region) => {
    const data = get()
    if (region?.id === data.region?.id) return
    set({ region, resourceGroup: null, ...clearRegionDependent(data) })
  },

  setResourceGroup: (resourceGroup) => {
    const data = get()
    if (resourceGroup?.id === data.resourceGroup?.id) return
    set({ resourceGroup, ...clearResourceGroupDependent(data) })
  },

  setLevel: (level) => {
    const data = get()
    if (level === data.level) return
    // The DB server is a different kind of server on each level (an Azure SQL server or a SQL VM), and an existing one is listed
    // with or without its instances: its choice and values start again. The RDS server is the same on both levels, so it stays.
    set({ level, existingDbServers: null, dbServer: emptyDbServer(), sqlInstance: { ...data.sqlInstance, instanceName: '' } })
  },
  setManualSqlServerName: (manualSqlServerName) => set({ manualSqlServerName }),

  setExistingRdsServers: (existingRdsServers) => set({ existingRdsServers }),
  setExistingDbServers: (existingDbServers) => set({ existingDbServers }),
  setRdsMode: (mode) => {
    if (get().rdsServer.mode === mode) return
    set({ rdsServer: emptyRdsServer(mode) })
  },
  selectExistingRds: (server) => set((s) => ({ rdsServer: { ...s.rdsServer, existing: server } })),
  patchRdsServer: (patch) => set((s) => ({ rdsServer: { ...s.rdsServer, ...patch } })),
  setRdsVmCount: (count) => set((s) => ({ rdsServer: { ...s.rdsServer, count: clampRdsVmCount(count) } })),
  setRdsPublicIp: (publicIp) => set((s) => ({ rdsServer: { ...s.rdsServer, publicIp } })),
  setDbMode: (mode) => {
    const data = get()
    if (data.dbServer.mode === mode) return
    set({ dbServer: emptyDbServer(mode), sqlInstance: { ...data.sqlInstance, instanceName: '' } })
  },
  selectExistingDb: (server) => {
    const data = get()
    if (server?.name === data.dbServer.existing?.name) return
    // An instance name was checked against the server that is no longer chosen.
    set({ dbServer: { ...data.dbServer, existing: server }, sqlInstance: { ...data.sqlInstance, instanceName: '' } })
  },
  patchDedicatedSqlVm: (patch) => set((s) => ({ dbServer: { ...s.dbServer, sqlVm: { ...s.dbServer.sqlVm, ...patch } } })),
  patchDedicatedAzureSql: (patch) => set((s) => ({ dbServer: { ...s.dbServer, azureSql: { ...s.dbServer.azureSql, ...patch } } })),
  setDbPublicIp: (publicIp) => set((s) => ({ dbServer: { ...s.dbServer, sqlVm: { ...s.dbServer.sqlVm, publicIp } } })),
  continueWithoutPublicIp: () =>
    set((s) => ({
      rdsServer: { ...s.rdsServer, publicIp: false },
      dbServer: { ...s.dbServer, sqlVm: { ...s.dbServer.sqlVm, publicIp: false } },
    })),

  setSqlInstanceRequired: (required) => set((s) => ({ sqlInstance: { required, instanceName: required ? s.sqlInstance.instanceName : '' } })),
  setSqlInstanceName: (instanceName) => set((s) => ({ sqlInstance: { ...s.sqlInstance, instanceName } })),
  patchLicense: (patch) => set((s) => ({ license: { ...s.license, ...patch } })),

  setAddOnEnabled: (key, enabled) =>
    set((s) => ({ [key]: enabled ? { ...s[key], enabled: true } : emptyAddOn() }) as Pick<InfraWizardData, AddOnKey>),
  patchAddOn: (key, patch) => set((s) => ({ [key]: { ...s[key], ...patch } }) as Pick<InfraWizardData, AddOnKey>),
  setExistingIisServers: (existingIisServers) => set({ existingIisServers }),
  setIisEnabled: (key, enabled) => set({ [key]: enabled ? { ...get()[key], enabled: true } : emptyIis() } as Pick<InfraWizardData, IisAddOnKey>),
  selectIisServer: (key, server) => set((s) => ({ [key]: { ...s[key], server } }) as Pick<InfraWizardData, IisAddOnKey>),
  setLinkVersion: (linkVersion) => set({ linkVersion }),
  chooseVmSize: (kind, entry) => set((s) => ({ vmSizeChoices: { ...s.vmSizeChoices, [kind]: entry } })),

  reset: () => set(emptyData()),
}))
